import { KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useKeyboardShortcut } from 'src/hooks';
import { IconName, Tooltip, CustomiseLearningFeaturesModal } from 'src/components';
import { hasTriggeredExtraClickFromKeyUpEvent } from 'src/apps/LearningSession/utils/hasTriggeredExtraClickFromKeyUpEvent';
import { SessionColor } from 'src/apps/LearningSession/sessionConfig';
import { TrackingApi } from 'src/api';
import { WebSessionType } from 'src/api/learningSessions/types';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { isEnglishLanguage } from 'src/utils/language/isEnglishLanguage';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import KeyboardShortcutDrawer from './KeyboardShortcutDrawer';
import { LearningToolsCoachmark } from './LearningToolsCoachmark';
import { useExitSession } from './useExitSession';
import * as S from './styles';

const msgs = defineMessages({
  exitSession: {
    id: 'learning_session_tooltip_exit',
  },
  keyboardShortcuts: {
    id: 'learning_session_tooltip_keyboard',
  },
  reportIssue: {
    id: 'report_issue_modal_title',
  },
  customiseFeatures: {
    id: 'learning_session_tooltip_customise',
    defaultMessage: 'Customise learning experience',
  },
  learningTools: {
    id: 'learning_session_learning_tools_button',
    defaultMessage: 'Learning Tools',
  },
});

export interface LearningSessionHeaderProps {
  sendCurrentLearningProgress: () => Promise<void>;
  sessionColor: SessionColor;
  sessionIcon: IconName;
  sessionType: WebSessionType;
  title: string;
  onReportIssueClick?: () => void;
  analyticsSessionId?: string;
  wordlistId?: string;
  showLearningToolsCoachmark?: boolean;
  onDismissLearningToolsCoachmark?: () => void;
}

export const LearningSessionHeader = ({
  sessionColor,
  sessionIcon,
  sessionType,
  sendCurrentLearningProgress,
  title,
  onReportIssueClick,
  analyticsSessionId,
  wordlistId,
  showLearningToolsCoachmark = false,
  onDismissLearningToolsCoachmark,
}: LearningSessionHeaderProps) => {
  const [keyboardShortcutsShown, setKeyboardShortcutsShown] = useState(false);
  const [customiseFeaturesShown, setCustomiseFeaturesShown] = useState(false);
  const intl = useIntl();
  const { user } = useUserContext();
  const { languagePair } = useCurrentLanguagePair();
  const showLearningToolsLabel = isEnglishLanguage(languagePair?.sourceLanguageLocale);
  const handleExitClick = useExitSession({
    sessionType,
    sendCurrentLearningProgress,
    analyticsSessionId,
  });
  const closeKeyboardDrawer = () => setKeyboardShortcutsShown(false);
  const toggleKeyboardDrawer = (event?: MouseEvent) => {
    if (hasTriggeredExtraClickFromKeyUpEvent(event)) return;
    setKeyboardShortcutsShown(!keyboardShortcutsShown);
  };

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event?.key === 'Enter' || event?.key === ' ') {
        event.stopPropagation();
        setKeyboardShortcutsShown(!keyboardShortcutsShown);
      }
    },
    [keyboardShortcutsShown],
  );

  useKeyboardShortcut('ctrl+/', () => setKeyboardShortcutsShown(!keyboardShortcutsShown));
  useKeyboardShortcut(
    'command+/',
    () => setKeyboardShortcutsShown(!keyboardShortcutsShown),
    true, // See command key bug with keyup: https://bugzilla.mozilla.org/show_bug.cgi?id=1299553
  );
  useKeyboardShortcut('esc', () => setKeyboardShortcutsShown(false));
  useKeyboardShortcut(
    'esc',
    showLearningToolsCoachmark ? onDismissLearningToolsCoachmark : undefined,
  );

  const handleCustomiseFeaturesClick = () => {
    onDismissLearningToolsCoachmark?.();

    if (!customiseFeaturesShown) {
      void TrackingApi.trackUserAction(
        'tab_opened',
        'learning_session',
        getLanguagePairId(),
        !!user?.isPro,
        {
          ...(wordlistId && { wordlist_id: wordlistId }),
        },
      );
    }

    setCustomiseFeaturesShown(!customiseFeaturesShown);
  };

  return (
    <S.Header $color={sessionColor} data-testid="learning-session-header">
      <S.HeaderContents>
        <S.IconReleaseTagWrapper>
          <S.SessionIcon name={sessionIcon} />
        </S.IconReleaseTagWrapper>
        {title && <S.Title>{title}</S.Title>}
        <S.HeaderIconsWrapper>
          <S.CustomiseFeaturesWrapper $coachmarkShown={showLearningToolsCoachmark}>
            <Tooltip
              disabled={showLearningToolsCoachmark}
              displayContents
              placement="bottom"
              tooltip={intl.formatMessage(msgs.customiseFeatures)}
            >
              <S.CustomiseFeaturesButton
                onClick={handleCustomiseFeaturesClick}
                data-testid="customiseFeaturesBtn"
                aria-label={intl.formatMessage(msgs.customiseFeatures)}
                $showLabel={showLearningToolsLabel}
                $coachmarkShown={showLearningToolsCoachmark}
              >
                <S.KeyboardShortcutIcon name="toggles" color={sessionColor} />
                {showLearningToolsLabel && (
                  <S.CustomiseFeaturesLabel>
                    {intl.formatMessage(msgs.learningTools)}
                  </S.CustomiseFeaturesLabel>
                )}
              </S.CustomiseFeaturesButton>
            </Tooltip>
            {showLearningToolsCoachmark && onDismissLearningToolsCoachmark && (
              <LearningToolsCoachmark onClose={onDismissLearningToolsCoachmark} />
            )}
          </S.CustomiseFeaturesWrapper>

          <Tooltip
            displayContents
            placement="bottom"
            tooltip={intl.formatMessage(msgs.keyboardShortcuts)}
          >
            <S.KeyboardIconButton
              onClick={toggleKeyboardDrawer}
              onKeyUp={onKeyUp}
              data-testid="keyboardShortcutDrawerBtn"
              aria-label={intl.formatMessage(msgs.keyboardShortcuts)}
            >
              <S.KeyboardShortcutIcon name="keyboard" color={sessionColor} />
            </S.KeyboardIconButton>
          </Tooltip>

          {onReportIssueClick && (
            <Tooltip
              displayContents
              placement="bottom"
              tooltip={intl.formatMessage(msgs.reportIssue)}
            >
              <S.IconButton
                onClick={onReportIssueClick}
                data-testid="reportIssueButton"
                aria-label={intl.formatMessage(msgs.reportIssue)}
              >
                <S.KeyboardShortcutIcon name="flag" color={sessionColor} />
              </S.IconButton>
            </Tooltip>
          )}

          <Tooltip
            displayContents
            placement="bottom"
            tooltip={intl.formatMessage(msgs.exitSession)}
          >
            <S.TransparentButton
              onClick={handleExitClick}
              data-testid="headerExitButton"
              aria-label={intl.formatMessage(msgs.exitSession)}
            >
              <S.XIcon name="close" color={sessionColor} />
            </S.TransparentButton>
          </Tooltip>
        </S.HeaderIconsWrapper>
        <KeyboardShortcutDrawer isShown={keyboardShortcutsShown} hide={closeKeyboardDrawer} />
        <CustomiseLearningFeaturesModal
          isOpen={customiseFeaturesShown}
          onClose={() => setCustomiseFeaturesShown(false)}
          wordlistId={wordlistId}
        />
      </S.HeaderContents>
    </S.Header>
  );
};
